import { graphql } from '../codegen'

// Re-export codegen types for consumers
export type {
  GetRobotArmQuery,
  GetShuttleCoordinateDefinitionsQuery,
  GetShuttleSystemQuery,
  KeepAliveTrainingSessionMutation,
  UpdateRobotDeviceMutation,
  TransportPositionStreamSubscription,
  InitializeTrainingMutation,
  UpdateRobotSettingsMutation,
  SetSafeRangeAxisMutation,
  RemoveSafeRangeAxisMutation,
  SetSafeRangeJointMutation,
  RemoveSafeRangeJointMutation,
} from '../codegen/graphql'

export const GetShuttleCoordinateDefinitionsDocument = graphql(/* GraphQL */ `
  query GetShuttleCoordinateDefinitions($input: ShuttleSystemInput!) {
    transportTraining {
      shuttleCoordinateDefinitions(input: $input) {
        name
        min
        max
      }
    }
  }
`)

export const GetShuttleSystemDocument = graphql(/* GraphQL */ `
  query GetShuttleSystem($input: ShuttleSystemInput!) {
    transportTraining {
      shuttleSystem(input: $input) {
        id
        homeLocation
        locations {
          locationName
          coordinates
          robotIds
        }
      }
    }
  }
`)

export const SetLocationDocument = graphql(/* GraphQL */ `
  mutation SetLocation($input: SetLocationInput!) {
    transportTraining {
      setLocation(input: $input) {
        id
        homeLocation
        locations {
          locationName
          coordinates
          robotIds
        }
      }
    }
  }
`)

export const RemoveLocationDocument = graphql(/* GraphQL */ `
  mutation RemoveLocation($input: RemoveLocationInput!) {
    transportTraining {
      removeLocation(input: $input) {
        id
        homeLocation
        locations {
          locationName
          coordinates
          robotIds
        }
      }
    }
  }
`)

export const SetHomeLocationDocument = graphql(/* GraphQL */ `
  mutation SetHomeLocation($input: SetHomeLocationInput!) {
    transportTraining {
      setHomeLocation(input: $input) {
        id
        homeLocation
        locations {
          locationName
          coordinates
          robotIds
        }
      }
    }
  }
`)

export const UpdateRobotDeviceDocument = graphql(/* GraphQL */ `
  mutation UpdateRobotDevice($input: UpdateRobotDeviceInput!) {
    updateRobotDevice(input: $input) {
      id
      connectedDevices {
        id
        name
      }
    }
  }
`)

export const GetRobotArmDocument = graphql(/* GraphQL */ `
  query GetRobotArm($input: RobotTrainingInput!) {
    transportTraining {
      robotArm(input: $input) {
        id
        homePosition {
          joints
        }
        settings {
          speedProfile
          blended
          railLength
          motionEnvelope {
            axes {
              axis
              min
              max
              safeMin
              safeMax
            }
            joints {
              jointIndex
              name
              min
              max
              safeMin
              safeMax
            }
          }
        }
        reachableInstruments {
          id
          trained
          slots {
            name
            number
            slotStatus
            transportableSlot
            isInterpolated
            interpolationRange
            paths {
              orientation
              entrance {
                waypoint {
                  ... on JointWaypoint {
                    joints
                  }
                  ... on CartesianWaypoint {
                    x
                    y
                    z
                    rail
                    rotation
                  }
                }
              }
              midpoints {
                waypoint {
                  ... on JointWaypoint {
                    joints
                  }
                  ... on CartesianWaypoint {
                    x
                    y
                    z
                    rail
                    rotation
                  }
                }
              }
              station {
                approachHeight
                orientation
                releaseWidth
                waypoint {
                  ... on JointWaypoint {
                    joints
                  }
                  ... on CartesianWaypoint {
                    x
                    y
                    z
                    rail
                    rotation
                  }
                }
              }
              pathMotionProfile {
                speedProfile
                straightLine
                blended
              }
            }
          }
        }
      }
    }
  }
`)

export const GetModulesForTrainingDocument = graphql(/* GraphQL */ `
  query GetModulesForTraining {
    modules {
      id
      name
      artifactName
      version
      type
      tasks
      device {
        id
        name
        vendor
        model
      }
    }
  }
`)

export const PositionStreamSubscriptionDocument = graphql(/* GraphQL */ `
  subscription TransportPositionStream(
    $input: TransportPositionStreamSubscriptionInput!
  ) {
    transportPositionStream(input: $input) {
      robotId
      position {
        cartesian {
          x
          y
          z
          rail
          rotation
        }
        joint {
          joints
        }
      }
    }
  }
`)

export const AddPathToSlotDocument = graphql(/* GraphQL */ `
  mutation AddPathToSlot($input: PathInput!) {
    transportTraining {
      addPathToSlot(input: $input)
    }
  }
`)

export const RemovePathFromSlotDocument = graphql(/* GraphQL */ `
  mutation RemovePathFromSlot($input: PathInput!) {
    transportTraining {
      removePathFromSlot(input: $input)
    }
  }
`)

export const InitializeTrainingDocument = graphql(/* GraphQL */ `
  mutation InitializeTraining($input: InitializeTrainingInput!) {
    transportTraining {
      initializeTraining(input: $input)
    }
  }
`)

export const ShutdownTrainingDocument = graphql(/* GraphQL */ `
  mutation ShutdownTraining($input: ShutdownTrainingInput!) {
    transportTraining {
      shutdownTraining(input: $input)
    }
  }
`)

export const SyncRobotInstrumentsDocument = graphql(/* GraphQL */ `
  mutation SyncRobotInstruments($input: RobotTrainingInput!) {
    transportTraining {
      syncRobotInstruments(input: $input)
    }
  }
`)

export const JogJointDocument = graphql(/* GraphQL */ `
  mutation JogJoint($input: JogJointInput!) {
    transportTraining {
      jogJoint(input: $input) {
        errors {
          __typename
          ... on JogFailedError {
            message
          }
        }
      }
    }
  }
`)

export const JogDirectionDocument = graphql(/* GraphQL */ `
  mutation JogDirection($input: JogDirectionInput!) {
    transportTraining {
      jogDirection(input: $input) {
        errors {
          __typename
          ... on JogFailedError {
            message
          }
        }
      }
    }
  }
`)

export const SetFreeModeDocument = graphql(/* GraphQL */ `
  mutation SetFreeMode($input: SetFreeModeInput!) {
    transportTraining {
      setFreeMode(input: $input)
    }
  }
`)

export const SetHomePositionDocument = graphql(/* GraphQL */ `
  mutation SetHomePosition($input: HomePositionInput!) {
    transportTraining {
      setHomePosition(input: $input) {
        joints
      }
    }
  }
`)

export const UpdateRobotSettingsDocument = graphql(/* GraphQL */ `
  mutation UpdateRobotSettings($input: UpdateRobotSettingsInput!) {
    transportTraining {
      updateRobotSettings(input: $input) {
        settings {
          speedProfile
          blended
          railLength
          motionEnvelope {
            axes {
              axis
              min
              max
              safeMin
              safeMax
            }
            joints {
              jointIndex
              name
              min
              max
              safeMin
              safeMax
            }
          }
        }
        errors {
          __typename
          ... on RobotSettingsFailedError {
            message
          }
        }
      }
    }
  }
`)

export const SetSafeRangeAxisDocument = graphql(/* GraphQL */ `
  mutation SetSafeRangeAxis($input: SetSafeRangeAxisInput!) {
    transportTraining {
      setSafeRangeAxis(input: $input) {
        motionEnvelope {
          axes {
            axis
            min
            max
            safeMin
            safeMax
          }
          joints {
            jointIndex
            name
            min
            max
            safeMin
            safeMax
          }
        }
        errors {
          __typename
          ... on RobotSettingsFailedError {
            message
          }
        }
      }
    }
  }
`)

export const RemoveSafeRangeAxisDocument = graphql(/* GraphQL */ `
  mutation RemoveSafeRangeAxis($input: RemoveSafeRangeAxisInput!) {
    transportTraining {
      removeSafeRangeAxis(input: $input) {
        motionEnvelope {
          axes {
            axis
            min
            max
            safeMin
            safeMax
          }
          joints {
            jointIndex
            name
            min
            max
            safeMin
            safeMax
          }
        }
        errors {
          __typename
          ... on RobotSettingsFailedError {
            message
          }
        }
      }
    }
  }
`)

export const SetSafeRangeJointDocument = graphql(/* GraphQL */ `
  mutation SetSafeRangeJoint($input: SetSafeRangeJointInput!) {
    transportTraining {
      setSafeRangeJoint(input: $input) {
        motionEnvelope {
          axes {
            axis
            min
            max
            safeMin
            safeMax
          }
          joints {
            jointIndex
            name
            min
            max
            safeMin
            safeMax
          }
        }
        errors {
          __typename
          ... on RobotSettingsFailedError {
            message
          }
        }
      }
    }
  }
`)

export const RemoveSafeRangeJointDocument = graphql(/* GraphQL */ `
  mutation RemoveSafeRangeJoint($input: RemoveSafeRangeJointInput!) {
    transportTraining {
      removeSafeRangeJoint(input: $input) {
        motionEnvelope {
          axes {
            axis
            min
            max
            safeMin
            safeMax
          }
          joints {
            jointIndex
            name
            min
            max
            safeMin
            safeMax
          }
        }
        errors {
          __typename
          ... on RobotSettingsFailedError {
            message
          }
        }
      }
    }
  }
`)

export const HomeDocument = graphql(/* GraphQL */ `
  mutation Home($input: HomeInput!) {
    transportTraining {
      home(input: $input)
    }
  }
`)

export const OpenGripperDocument = graphql(/* GraphQL */ `
  mutation OpenGripper($input: OpenGripperInput!) {
    transportTraining {
      openGripper(input: $input)
    }
  }
`)

export const CloseGripperDocument = graphql(/* GraphQL */ `
  mutation CloseGripper($input: CloseGripperInput!) {
    transportTraining {
      closeGripper(input: $input)
    }
  }
`)

export const TestPickDocument = graphql(/* GraphQL */ `
  mutation TestPick($input: TestPickInput!) {
    transportTraining {
      testPick(input: $input) {
        errors {
          __typename
          ... on WaypointUnreachableError {
            code
          }
        }
      }
    }
  }
`)

export const TestPlaceDocument = graphql(/* GraphQL */ `
  mutation TestPlace($input: TestPlaceInput!) {
    transportTraining {
      testPlace(input: $input) {
        errors {
          __typename
          ... on WaypointUnreachableError {
            code
          }
        }
      }
    }
  }
`)

export const SetSlotStatusDocument = graphql(/* GraphQL */ `
  mutation SetSlotStatus($input: SetSlotStatusInput!) {
    transportTraining {
      setSlotStatus(input: $input) {
        name
        slotStatus
      }
    }
  }
`)

export const TestWaypointDocument = graphql(/* GraphQL */ `
  mutation TestWaypoint($input: TestWaypointInput!) {
    transportTraining {
      testWaypoint(input: $input) {
        errors {
          __typename
          ... on WaypointUnreachableError {
            code
          }
        }
      }
    }
  }
`)

export const UpdateSlotPathsDocument = graphql(/* GraphQL */ `
  mutation UpdateSlotPaths($input: UpdateSlotPathsInput!) {
    transportTraining {
      updateSlotPaths(input: $input) {
        orientation
        entrance {
          waypoint {
            ... on JointWaypoint {
              joints
            }
            ... on CartesianWaypoint {
              x
              y
              z
              rail
              rotation
            }
          }
        }
        midpoints {
          waypoint {
            ... on JointWaypoint {
              joints
            }
            ... on CartesianWaypoint {
              x
              y
              z
              rail
              rotation
            }
          }
        }
        station {
          approachHeight
          orientation
          releaseWidth
          waypoint {
            ... on JointWaypoint {
              joints
            }
            ... on CartesianWaypoint {
              x
              y
              z
              rail
              rotation
            }
          }
        }
        pathMotionProfile {
          speedProfile
          straightLine
          blended
        }
      }
    }
  }
`)

export const KeepAliveTrainingSessionDocument = graphql(/* GraphQL */ `
  mutation KeepAliveTrainingSession($deviceId: UUID!) {
    transportTraining {
      keepAliveTrainingSession(deviceId: $deviceId)
    }
  }
`)
